import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { clienteService, prestamoService } from '../../services/api';
import '../../styles/Clientes.css';
import { usePermisosGuard } from '../../hooks/usePermisosGuard';

const EditarPrestamo = () => {
  usePermisosGuard('prestamos', 'editar');
  const navigate = useNavigate();
  const { codigo } = useParams();
  const getRoute = (path) => codigo ? `/${codigo}${path}` : path;
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [prestamos, setPrestamos] = useState([]);
  const [prestamoSeleccionado, setPrestamoSeleccionado] = useState(null);
  const [searchId, setSearchId] = useState('');
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [prestamosPorCliente, setPrestamosPorCliente] = useState([]);
  const [rutas, setRutas] = useState([]);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [mostrarDropdown, setMostrarDropdown] = useState(false);
  const [clienteObj, setClienteObj] = useState(null);

  useEffect(() => {
    loadPrestamos();
    loadClientes();
    loadRutas();
    const id = searchParams.get('id');
    if (id) {
      setSearchId(id);
      buscarPorId(id);
    }
  }, [searchParams]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('div')) {
        setMostrarDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPrestamos = async () => {
    try {
      const response = await prestamoService.getPrestamos({ estado: 'activo', per_page: 1000 });
      setPrestamos(response.data.prestamos || []);
    } catch (err) {
      console.error('Error al cargar préstamos:', err);
    }
  };

  const loadClientes = async () => {
    try {
      const response = await clienteService.getClientes({ per_page: 1000 });
      setClientes(response.data.clientes || []);
    } catch (err) {
      console.error('Error al cargar clientes:', err);
    }
  };

  const loadRutas = async () => {
    try {
      // Asumiendo que tienes un servicio para cargar rutas
      // Si no, puedes crear uno o usar datos estáticos
      setRutas([
        { id: 1, nombre: 'Ruta 1' },
        { id: 2, nombre: 'Ruta 2' },
        { id: 3, nombre: 'Ruta 3' },
        { id: 4, nombre: 'Ruta 4' },
        { id: 5, nombre: 'Ruta 5' }
      ]);
    } catch (err) {
      console.error('Error al cargar rutas:', err);
    }
  };

  const buscarPorId = async (id) => {
    if (!id) {
      setError('Ingrese un ID de préstamo');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await prestamoService.getPrestamo(id);
      setPrestamoSeleccionado(response.data);
    } catch (err) {
      console.error('Error:', err);
      setError('Préstamo no encontrado');
      setPrestamoSeleccionado(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBuscar = () => {
    buscarPorId(searchId);
  };

  const clientesFiltrados = clientes.filter(cliente => {
    const termino = busquedaCliente.toLowerCase().trim();
    if (!termino) return true;

    return (
      (cliente.nombres && cliente.nombres.toLowerCase().includes(termino)) ||
      (cliente.apellidos && cliente.apellidos.toLowerCase().includes(termino)) ||
      (cliente.cedula && cliente.cedula.includes(termino)) ||
      (cliente.apodo && cliente.apodo.toLowerCase().includes(termino)) ||
      cliente.id.toString().includes(termino) ||
      (cliente.nombres && cliente.apellidos &&
       `${cliente.nombres} ${cliente.apellidos}`.toLowerCase().includes(termino))
    );
  }).slice(0, 10);

  const seleccionarCliente = async (cliente) => {
    setClienteObj(cliente);
    setBusquedaCliente(`${cliente.nombres} ${cliente.apellidos} - ${cliente.cedula}`);
    setMostrarDropdown(false);
    setClienteSeleccionado(cliente.id);
    setPrestamoSeleccionado(null);
    setError('');

    try {
      setLoading(true);
      const response = await prestamoService.getPrestamos({
        cliente_id: cliente.id,
        estado: 'activo',
        per_page: 1000
      });
      setPrestamosPorCliente(response.data.prestamos || []);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar préstamos del cliente');
    } finally {
      setLoading(false);
    }
  };

  const limpiarCliente = () => {
    setClienteObj(null);
    setClienteSeleccionado('');
    setBusquedaCliente('');
    setPrestamosPorCliente([]);
    setPrestamoSeleccionado(null);
  };

  const handleSeleccionarPrestamo = async (prestamoId) => {
    try {
      setLoading(true);
      setError('');
      const response = await prestamoService.getPrestamo(prestamoId);
      setPrestamoSeleccionado(response.data);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar el préstamo');
    } finally {
      setLoading(false);
    }
  };

  const handleGuardar = async () => {
    if (!prestamoSeleccionado) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await prestamoService.updatePrestamo(prestamoSeleccionado.id, {
        ruta_id: prestamoSeleccionado.ruta_id,
        modalidad: prestamoSeleccionado.modalidad,
        estado: prestamoSeleccionado.estado,
        dia_semana: prestamoSeleccionado.dia_semana,
        fecha_inicio: prestamoSeleccionado.fecha_inicio,      // AGREGADO: Fecha de desembolso
        fecha_efectividad: prestamoSeleccionado.fecha_efectividad  // AGREGADO: Fecha del primer pago
      });

      setSuccess('Préstamo actualizado exitosamente');

      setTimeout(() => {
        navigate(getRoute('/prestamos/lista'));
      }, 1500);

    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.error || 'Error al actualizar el préstamo');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPrestamoSeleccionado(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatMoney = (amount) => {
    return `$${(amount || 0).toLocaleString('es-DO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Extraer solo la parte de fecha sin conversión de zona horaria
    return dateString.split('T')[0];
  };

  return (
    <div className="clientes-page">
      <div className="clientes-header">
        <h1>Editar Préstamo</h1>
        <button
          className="btn"
          onClick={() => navigate(getRoute('/prestamos'))}
          style={{background: '#666'}}
        >
          ← Volver
        </button>
      </div>

      {error && <div className="banner error">{error}</div>}
      {success && <div className="banner success">{success}</div>}

      {/* Búsqueda */}
      <div className="card" style={{marginBottom: '2rem'}}>
        <div className="card-title" style={{background: '#3498db'}}>
          Buscar Préstamo
        </div>
        <div style={{padding: '1rem'}}>
          {/* Búsqueda por ID */}
          <div style={{display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1.5rem'}}>
            <div style={{flex: 1}}>
              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 600}}>
                Préstamo ID
              </label>
              <input
                type="number"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                placeholder="Ingrese el ID del préstamo"
                className="form-control"
              />
            </div>
            <button
              onClick={handleBuscar}
              className="btn"
              style={{background: '#3498db', color: '#fff', padding: '0.75rem 2rem'}}
              disabled={loading}
            >
              Buscar por ID
            </button>
          </div>

          {/* Separador */}
          <div style={{
            borderTop: '2px dashed #ddd',
            margin: '1.5rem 0',
            position: 'relative'
          }}>
            <span style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#fff',
              padding: '0 1rem',
              color: '#666',
              fontWeight: 600
            }}>
              O
            </span>
          </div>

          {/* Búsqueda por Cliente */}
          <div>
            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 600}}>
              Buscar por Cliente
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nombre, cédula, apodo o ID..."
                value={busquedaCliente}
                onChange={(e) => {
                  setBusquedaCliente(e.target.value);
                  setMostrarDropdown(true);
                }}
                onFocus={() => setMostrarDropdown(true)}
                disabled={loading}
              />
              {clienteObj && (
                <button
                  type="button"
                  onClick={limpiarCliente}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#6c757d'
                  }}
                >
                  ×
                </button>
              )}

              {mostrarDropdown && busquedaCliente && clientesFiltrados.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  {clientesFiltrados.map((cliente) => (
                    <div
                      key={cliente.id}
                      onClick={() => seleccionarCliente(cliente)}
                      style={{
                        padding: '10px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      {cliente.foto_url ? (
                        <img
                          src={cliente.foto_url}
                          alt={cliente.nombres}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#007bff',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold'
                        }}>
                          {cliente.nombres?.charAt(0)}{cliente.apellidos?.charAt(0)}
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold' }}>
                          {cliente.nombres} {cliente.apellidos}
                          {cliente.apodo && (
                            <span style={{
                              marginLeft: '8px',
                              color: '#007bff',
                              fontStyle: 'italic',
                              fontWeight: 'normal'
                            }}>
                              ({cliente.apodo})
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#666' }}>
                          Cédula: {cliente.cedula} • ID: {cliente.id}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Lista de préstamos del cliente */}
          {prestamosPorCliente.length > 0 && (
            <div style={{marginTop: '1rem'}}>
              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 600}}>
                Préstamos del Cliente
              </label>
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}>
                {prestamosPorCliente.map(prestamo => (
                  <div
                    key={prestamo.id}
                    onClick={() => handleSeleccionarPrestamo(prestamo.id)}
                    style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      background: prestamoSeleccionado?.id === prestamo.id ? '#e3f2fd' : '#fff',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (prestamoSeleccionado?.id !== prestamo.id) {
                        e.target.style.background = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (prestamoSeleccionado?.id !== prestamo.id) {
                        e.target.style.background = '#fff';
                      }
                    }}
                  >
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <div>
                        <strong>ID: {prestamo.id}</strong> - {prestamo.modalidad}
                      </div>
                      <div style={{textAlign: 'right'}}>
                        <div style={{fontWeight: 'bold', color: '#27ae60'}}>
                          ${prestamo.total.toLocaleString('es-DO', {minimumFractionDigits: 2})}
                        </div>
                        <span className={`badge badge-${
                          prestamo.estado === 'activo' ? 'success' :
                          prestamo.estado === 'pagado' ? 'info' : 'warning'
                        }`}>
                          {prestamo.estado}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {clienteSeleccionado && prestamosPorCliente.length === 0 && !loading && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              color: '#856404'
            }}>
              Este cliente no tiene préstamos registrados
            </div>
          )}
        </div>
      </div>

      {/* Formulario de Edición */}
      {prestamoSeleccionado && (
        <>
          {/* Información de Reenganche - Si este préstamo es un reenganche (tiene prestamo_origen) */}
          {prestamoSeleccionado.prestamo_origen && (
            <div className="card" style={{marginBottom: '2rem', borderLeft: '4px solid #3498db'}}>
              <div style={{
                padding: '1.5rem',
                background: '#e3f2fd',
                borderRadius: '4px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  <span style={{ fontSize: '1.5rem' }}>🔄</span>
                  <strong style={{ color: '#1976d2', fontSize: '1.1rem' }}>
                    Información de Reenganche
                  </strong>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginTop: '0.5rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                      Reenganchado desde el préstamo:
                    </div>
                    <div style={{ fontWeight: 'bold', color: '#e67e22', fontSize: '1.1rem' }}>
                      #{prestamoSeleccionado.prestamo_origen.id}
                    </div>
                  </div>
                  {prestamoSeleccionado.prestamo_origen.fecha_reenganche && (
                    <div>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                        Fecha del reenganche:
                      </div>
                      <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                        {new Date(prestamoSeleccionado.prestamo_origen.fecha_reenganche).toLocaleDateString('es-DO', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit'
                        })}
                      </div>
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                      Monto anterior:
                    </div>
                    <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                      RD$ {prestamoSeleccionado.prestamo_origen.monto_prestado.toLocaleString('es-DO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                      Monto nuevo:
                    </div>
                    <div style={{ fontWeight: 'bold', fontSize: '1rem', color: '#27ae60' }}>
                      RD$ {prestamoSeleccionado.monto_prestado.toLocaleString('es-DO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Información de Reenganche - Si este préstamo fue reenganchado (tiene reemplazado_por) */}
          {(prestamoSeleccionado.estado === 'reenganchado' || prestamoSeleccionado.estado === 'refinanciado') && prestamoSeleccionado.prestamo_reemplazo && (
            <div className="card" style={{marginBottom: '2rem', borderLeft: '4px solid #e67e22'}}>
              <div style={{
                padding: '1.5rem',
                background: '#fff3e0',
                borderRadius: '4px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  <span style={{ fontSize: '1.5rem' }}>📋</span>
                  <div>
                    <strong style={{ color: '#e67e22', fontSize: '1.1rem', display: 'block' }}>
                      Estado: {prestamoSeleccionado.estado === 'reenganchado' ? 'Reenganchado' : 'Refinanciado'}
                    </strong>
                  </div>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginTop: '0.5rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                      Reemplazado por préstamo:
                    </div>
                    <div style={{ fontWeight: 'bold', color: '#3498db', fontSize: '1.1rem' }}>
                      #{prestamoSeleccionado.prestamo_reemplazo.id}
                    </div>
                  </div>
                  {prestamoSeleccionado.prestamo_reemplazo.fecha_reenganche && (
                    <div>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                        Fecha:
                      </div>
                      <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                        {new Date(prestamoSeleccionado.prestamo_reemplazo.fecha_reenganche).toLocaleDateString('es-DO', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit'
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.7)',
                  borderRadius: '4px',
                  borderLeft: '3px solid #f39c12'
                }}>
                  <p style={{ margin: 0, color: '#666', fontSize: '0.9rem', fontStyle: 'italic' }}>
                    <strong>Nota:</strong> Este préstamo forma parte del historial del cliente.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="card" style={{marginBottom: '2rem'}}>
            <div className="card-title" style={{background: '#27ae60'}}>
              Información del Préstamo (Campos Editables)
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Cliente (Solo lectura)</label>
                <input
                  type="text"
                  value={`${prestamoSeleccionado.cliente?.nombres || ''} ${prestamoSeleccionado.cliente?.apellidos || ''}`}
                  disabled
                  className="form-control"
                  style={{background: '#f5f5f5'}}
                />
              </div>

              <div className="form-group">
                <label>Préstamo ID (Solo lectura)</label>
                <input
                  type="text"
                  value={prestamoSeleccionado.id}
                  disabled
                  className="form-control"
                  style={{background: '#f5f5f5'}}
                />
              </div>

              <div className="form-group">
                <label>Ruta *</label>
                <select
                  name="ruta_id"
                  value={prestamoSeleccionado.ruta_id || ''}
                  onChange={handleChange}
                  className="form-control"
                  style={{background: '#fff', fontWeight: 'bold'}}
                >
                  <option value="">--- Seleccione ---</option>
                  {rutas.map(ruta => (
                    <option key={ruta.id} value={ruta.id}>
                      {ruta.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Modalidad *</label>
                <select
                  name="modalidad"
                  value={prestamoSeleccionado.modalidad}
                  onChange={handleChange}
                  className="form-control"
                  style={{background: '#fff', fontWeight: 'bold'}}
                >
                  <option value="Diario">Diario</option>
                  <option value="Semanal">Semanal</option>
                  <option value="Quincenal">Quincenal</option>
                  <option value="Mensual">Mensual</option>
                </select>
              </div>

              <div className="form-group">
                <label>Estado *</label>
                <select
                  name="estado"
                  value={prestamoSeleccionado.estado}
                  onChange={handleChange}
                  className="form-control"
                  style={{background: '#fff', fontWeight: 'bold'}}
                >
                  <option value="activo">Activo</option>
                  <option value="pagado">Pagado</option>
                  <option value="cancelado">Cancelado</option>
                  <option value="reenganchado">Reenganchado</option>
                </select>
              </div>

              <div className="form-group">
                <label>Día de la Semana</label>
                <select
                  name="dia_semana"
                  value={prestamoSeleccionado.dia_semana || ''}
                  onChange={handleChange}
                  className="form-control"
                  style={{background: '#fff', fontWeight: 'bold'}}
                >
                  <option value="">--- Seleccione ---</option>
                  <option value="Lunes">Lunes</option>
                  <option value="Martes">Martes</option>
                  <option value="Miércoles">Miércoles</option>
                  <option value="Jueves">Jueves</option>
                  <option value="Viernes">Viernes</option>
                  <option value="Sábado">Sábado</option>
                  <option value="Domingo">Domingo</option>
                </select>
              </div>

              <div className="form-group">
                <label>Tipo (Solo lectura)</label>
                <input
                  type="text"
                  value={prestamoSeleccionado.tipo}
                  disabled
                  className="form-control"
                  style={{background: '#f5f5f5'}}
                />
              </div>

              <div className="form-group">
                <label>FECHA DE DESEMBOLSO *</label>
                <input
                  type="date"
                  name="fecha_inicio"
                  value={formatDate(prestamoSeleccionado.fecha_inicio)}
                  onChange={handleChange}
                  className="form-control"
                  style={{background: '#fff', fontWeight: 'bold', border: '2px solid #27ae60'}}
                />
                <small style={{color: '#666', fontSize: '0.85rem', marginTop: '4px', display: 'block'}}>
                  📤 Fecha en que se entrega el dinero al cliente
                </small>
              </div>

              <div className="form-group">
                <label>FECHA DEL PRIMER PAGO *</label>
                <input
                  type="date"
                  name="fecha_efectividad"
                  value={formatDate(prestamoSeleccionado.fecha_efectividad)}
                  onChange={handleChange}
                  className="form-control"
                  style={{background: '#fff', fontWeight: 'bold', border: '2px solid #27ae60'}}
                />
                <small style={{color: '#666', fontSize: '0.85rem', marginTop: '4px', display: 'block'}}>
                  📥 Fecha en que el cliente debe pagar su primera cuota
                </small>
              </div>
            </div>

            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}>
              <strong>ℹ️ Campos Editables:</strong> Ruta, Modalidad, Estado, Día de la Semana, Fecha de Desembolso y Fecha del Primer Pago
            </div>
          </div>

          <div className="card" style={{marginBottom: '2rem'}}>
            <div className="card-title" style={{background: '#95a5a6'}}>
              Detalles Financieros (Solo Lectura)
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Capital</label>
                <input
                  type="text"
                  value={formatMoney(prestamoSeleccionado.capital)}
                  disabled
                  className="form-control"
                  style={{background: '#f5f5f5'}}
                />
              </div>

              <div className="form-group">
                <label>Tasa</label>
                <input
                  type="text"
                  value={`${prestamoSeleccionado.tasa}%`}
                  disabled
                  className="form-control"
                  style={{background: '#f5f5f5'}}
                />
              </div>

              <div className="form-group">
                <label>Interés</label>
                <input
                  type="text"
                  value={formatMoney(prestamoSeleccionado.interes)}
                  disabled
                  className="form-control"
                  style={{background: '#f5f5f5'}}
                />
              </div>

              <div className="form-group">
                <label>Total</label>
                <input
                  type="text"
                  value={formatMoney(prestamoSeleccionado.total)}
                  disabled
                  className="form-control"
                  style={{background: '#f5f5f5'}}
                />
              </div>

              <div className="form-group">
                <label>Cuotas</label>
                <input
                  type="text"
                  value={prestamoSeleccionado.cuotas}
                  disabled
                  className="form-control"
                  style={{background: '#f5f5f5'}}
                />
              </div>

              <div className="form-group">
                <label>Cuota Semanal</label>
                <input
                  type="text"
                  value={formatMoney(prestamoSeleccionado.cuota_semanal || 0)}
                  disabled
                  className="form-control"
                  style={{background: '#f5f5f5'}}
                />
              </div>
            </div>
          </div>

          {/* Botones */}
          <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
            <button
              onClick={() => navigate(getRoute('/prestamos'))}
              className="btn"
              style={{background: '#95a5a6'}}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              onClick={handleGuardar}
              className="btn"
              style={{background: '#27ae60', color: '#fff'}}
              disabled={loading}
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default EditarPrestamo;
